// ===============================
// src/ListeGardeValideeCore.jsx
// ===============================
// Composant CORE : affichage de la liste de garde validée
// - Utilisé par 2 wrappers : médecin (lecture seule) / responsable (avec actions)
// - Responsable + ASTREINTE_THORACIQUE : affichage riche (blocs, badges, compteurs, quadrimestre)
// - Médecin : affichage simple (date + nom du médecin)
// ===============================

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { identifierBlocs } from "./algo-astreintes-thoraciques.js";
import { confirmDialog, notify } from "./ui/dialogService.js";
import { apiFetch } from "./apiMedecins.js";
import { isFrenchPublicHoliday } from "./frenchHolidays.mjs";
import { useAuth } from "./contexts/AuthContext.jsx";
import { canManageGuardList, manualJuniorListPath, juniorListReadSlug } from "./ui/juniorListAccess.js";
import { withResponsibleBridgeContext } from "./responsableBack.js";

/** Liste ouverte depuis le hub /multisecteur/ : le retour doit quitter la SPA Port-Royal. */
function goPlanningListBack(targetPath, navigate) {
  const raw = String(targetPath || "").trim();
  if (!raw || !raw.startsWith("/")) return;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return;
    const rel = `${u.pathname}${u.search}${u.hash}`;
    if (rel.startsWith("/multisecteur/")) {
      window.location.assign(rel);
      return;
    }
    navigate(rel);
  } catch {
    /* ignore */
  }
}

export default function ListeGardeValideeCore({
  topBackPath,
  topBackLabel,
  title,
  subtitle,
  variant = "medecin", // "medecin" | "responsable"
  mois: moisProp, // ✅ Prop optionnelle : si fournie, l'utiliser (cas ListeGardeValideeMedecin)
  typeGarde = "PR2", // ✅ Type de garde pour les retours corrects (PR1, PR2, ASTREINTE_THORACIQUE, OPH)
}) {
  const { user } = useAuth();
  const canConstituteList = canManageGuardList(user, typeGarde);
  const juniorReadSlug = juniorListReadSlug(user, typeGarde);
  const navigate = useNavigate();
  const handleTopBack = useCallback(() => {
    goPlanningListBack(topBackPath, navigate);
  }, [topBackPath, navigate]);
  const isResponsable = variant === "responsable";
  const isTho = typeGarde === "ASTREINTE_THORACIQUE";
  const isOrthoJunior = typeGarde === "ORTHOPEDIE_JUNIOR";
  const isAstreinte = isTho || isOrthoJunior;
  const backLabel = isOrthoJunior ? "Retour à la gestion des astreintes" : topBackLabel;

  // ✅ Mois piloté par l'URL OU par la prop
  const params = new URLSearchParams(window.location.search);
  const lockSecteur = params.get("lockSecteur") === "1";
  const rawBack = params.get("back") || "";
  const withCurrentBack = (path) => {
    let next = path;
    if (lockSecteur && !/[?&]lockSecteur=/.test(next)) {
      const sep = next.includes("?") ? "&" : "?";
      next = `${next}${sep}lockSecteur=1`;
    }
    if (rawBack && !/[?&]back=/.test(next)) {
      const sep = next.includes("?") ? "&" : "?";
      next = `${next}${sep}back=${encodeURIComponent(rawBack)}`;
    }
    return next;
  };
  const defaultMois = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  }, []);
  // ✅ Utiliser la prop si fournie, sinon l'URL, sinon le mois courant
  const MOIS = moisProp || params.get("mois") || defaultMois;

  const [yStr, mStr] = String(MOIS).split("-");
  const YEAR = parseInt(yStr, 10) || 2026;
  const MONTH = parseInt(mStr, 10) || 2; // 1..12

  const monthLabel = useMemo(() => {
    try {
      const d = new Date(YEAR, MONTH - 1, 1);
      let s = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
      }).format(d);
      return s.charAt(0).toUpperCase() + s.slice(1);
    } catch {
      return `${String(MONTH).padStart(2, "0")}/${YEAR}`;
    }
  }, [YEAR, MONTH]);

  // ✅ Label lisible pour le type d'activité
  const typeLabel = useMemo(() => {
    switch (typeGarde) {
      case "PR1": return "Port Royal 1";
      case "PR2": return "Port Royal 2";
      case "DIGESTIF": return "Digestif";
      case "REANIMATION": return "Réanimation";
      case "ASTREINTE_THORACIQUE": return "Astreinte Thoracique";
      case "OPH": return "Ophtalmologie";
      case "ORTHOPEDIE_JUNIOR": return "Orthopédie junior";
      default: return typeGarde;
    }
  }, [typeGarde]);

  // ═══════════════════════════════════════════════════
  // ÉTATS
  // ═══════════════════════════════════════════════════
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [cancelMsg, setCancelMsg] = useState(null);
  const [estValidee, setEstValidee] = useState(null);
  const [notPublished, setNotPublished] = useState(false);
  const [showNotPublishedModal, setShowNotPublishedModal] = useState(false);

  // ✅ Données enrichies pour le mode responsable + thoracique
  const [mapKeyToOrigine, setMapKeyToOrigine] = useState({});
  const [nbQuadriParMed, setNbQuadriParMed] = useState({});

  // ═══════════════════════════════════════════════════
  // BLOCS (weekend/férié/pont) — thoracique responsable uniquement
  // ═══════════════════════════════════════════════════
  const joursEnBlocSet = useMemo(() => {
    if (!isTho || !isResponsable) return new Set();
    const tousLesJours = [];
    for (let m = 5; m <= 8; m++) {
      const nbJours = new Date(2026, m, 0).getDate();
      for (let d = 1; d <= nbJours; d++) {
        tousLesJours.push(`2026-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
      }
    }
    const { joursEnBloc } = identifierBlocs(tousLesJours);
    return joursEnBloc;
  }, [isTho, isResponsable]);

  const isJourBloc = useMemo(() => {
    if (!isTho || !isResponsable) return () => false;
    return (ds) => joursEnBlocSet.has(ds);
  }, [isTho, isResponsable, joursEnBlocSet]);

  // ═══════════════════════════════════════════════════
  // NAVIGATION QUADRIMESTRE — thoracique responsable uniquement
  // N'afficher la barre de navigation que si le mois consulté fait partie du quadrimestre.
  // ═══════════════════════════════════════════════════
  const QUADRIMESTRE = ["2026-05", "2026-06", "2026-07", "2026-08"];
  const moisQuadri = useMemo(() => {
    if (!isTho || !isResponsable) return [];
    // N'afficher que si le mois affiché fait partie du quadrimestre
    if (!QUADRIMESTRE.includes(MOIS)) return [];
    return QUADRIMESTRE;
  }, [isTho, isResponsable, MOIS]);

  // ═══════════════════════════════════════════════════
  // UTILITAIRES
  // ═══════════════════════════════════════════════════
  const jourSemaine = (dateStr) => {
    const jours = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
    const d = new Date(dateStr);
    return jours[d.getDay()];
  };

  // ═══════════════════════════════════════════════════
  // TITRES
  // ═══════════════════════════════════════════════════
  const displayTitle = useMemo(() => {
    if (isOrthoJunior) return `Astreintes junior d’orthopédie${estValidee ? " validées" : ""} — ${monthLabel}`;
    if (!isResponsable && notPublished) {
      return isTho
        ? `Liste d'astreintes ${typeLabel} (${monthLabel})`
        : `Liste de garde ${typeLabel} (${monthLabel})`;
    }
    if (estValidee === false) {
      return `Liste brute ${typeLabel} (${monthLabel})`;
    }
    return title ? `${title} ${typeLabel} — ${monthLabel}` : `Liste de garde validée ${typeLabel} — ${monthLabel}`;
  }, [isResponsable, notPublished, estValidee, title, monthLabel, typeLabel, isTho, isOrthoJunior]);

  const displaySubtitle = useMemo(() => {
    if (isOrthoJunior) return estValidee
      ? "Liste validée. Samedis, dimanches et jours fériés."
      : "Liste non validée. Samedis, dimanches et jours fériés.";
    if (!isResponsable && notPublished) {
      return isTho
        ? "La liste d'astreintes n'est pas encore validée par le responsable."
        : "La liste de garde n'est pas encore validée par le responsable.";
    }
    return estValidee === false
      ? "Liste brute (non validée). Visible tant qu'aucune liste validée n'est publiée."
      : subtitle;
  }, [isResponsable, notPublished, estValidee, subtitle, isTho, isOrthoJunior]);

  const loadingLabel = isAstreinte
    ? "Chargement de la liste d'astreintes validée..."
    : (estValidee === false ? "Chargement de la liste brute..." : "Chargement de la liste de garde validée...");

  // ═══════════════════════════════════════════════════
  // CHARGEMENT DES DONNÉES
  // ═══════════════════════════════════════════════════
  useEffect(() => {
    const load = async () => {
      try {
        setRows([]);
        setLoading(true);
        setError(null);
        setCancelMsg(null);
        setNotPublished(false);
        setShowNotPublishedModal(false);
        setEstValidee(null);
        setMapKeyToOrigine({});

        // ✅ URLs adaptées selon le type de garde
        const etatUrl = `/api/v2/liste_gardes/est_validee/${MOIS}?type=${encodeURIComponent(typeGarde)}`;
        const listeUrl = isTho
          ? `/api/v2/listes-gardes-astreinte-thoracique/${MOIS}`
          : `/api/v2/liste_gardes/${MOIS}?type=${encodeURIComponent(typeGarde)}`;
        const medUrl = juniorReadSlug
          ? `/api/v2/medecins-gardes-${juniorReadSlug}`
          : isTho
          ? "/api/v2/medecins-astreintes-thoracique"
          : isOrthoJunior
          ? "/api/v2/medecins-astreintes-orthopedie-junior?catalogue=1"
          : typeGarde === "PR1"
          ? "/api/v2/medecins-gardes-pr1"
          : typeGarde === "DIGESTIF"
          ? "/api/v2/medecins-gardes-digestif"
          : typeGarde === "ORTHOPEDIE"
          ? "/api/v2/medecins-astreintes-orthopedie"
          : "/api/v2/medecins-gardes-pr2";
        const desidUrl = juniorReadSlug
          ? `/api/v2/desiderata-${juniorReadSlug}/${MOIS}`
          : isTho
          ? `/api/v2/desiderata-astreinte-thoracique/${MOIS}`
          : `/api/v2/desiderata-pr2/${MOIS}`;

        const [etatRes, listeRes, medRes, desidRes] = await Promise.all([
          apiFetch(etatUrl),
          apiFetch(listeUrl),
          apiFetch(medUrl),
          isOrthoJunior ? Promise.resolve(null) : apiFetch(desidUrl),
        ]);

        // État validée/brute
        if (etatRes.ok) {
          const etat = (await etatRes.json()) || {};
          const v = Boolean(etat.estValidee ?? etat.valide);
          setEstValidee(v);
        } else {
          setEstValidee(null);
        }

        // Cas NOT_PUBLISHED
        if (!listeRes.ok) {
          if (listeRes.status === 404) {
            let payload = null;
            try { payload = await listeRes.json(); } catch { payload = null; }

            if (payload?.reason === "NOT_PUBLISHED") {
              if (isResponsable) {
                navigate(withCurrentBack(`/responsable/gardes-brute?mois=${encodeURIComponent(MOIS)}&type=${encodeURIComponent(typeGarde)}`));
                return;
              }
              setNotPublished(true);
              setShowNotPublishedModal(true);
              setRows([]);
              setError(null);
              setLoading(false);
              return;
            }
          }
          setRows([]);
          throw new Error(`HTTP ${listeRes.status} lors du chargement de la liste`);
        }

        const liste = (await listeRes.json()) || {};

        // Médecins
        const medecins = medRes.ok ? (await medRes.json()) || [] : [];
        const keyFromMed = (m) => `${m.nom}_${m.prenom}`;
        const labelFromMed = (m) => `${m.nom} ${m.prenom}`;

        const mapKeyToLabel = {};
        const origineMap = {};
        medecins.forEach((m) => {
          const k = keyFromMed(m);
          mapKeyToLabel[k] = labelFromMed(m);
          origineMap[k] = m.origine || "interieur";
        });
        setMapKeyToOrigine(origineMap);

        // Désidératas
        const desiderata = desidRes?.ok ? (await desidRes.json()) || [] : [];
        const desidByKey = {};
        for (const item of desiderata) {
          if (!item || !item.nom || !item.prenom) continue;
          const key = `${item.nom}_${item.prenom}`;
          desidByKey[key] = {
            nbGardes: typeof item.nbGardes === "number" ? item.nbGardes : parseInt(item.nbGardes, 10) || 0,
            souhaits: item.valeurs || {},
          };
        }

        // Comptage mensuel
        const nbRealiseesParMed = {};
        Object.values(liste).forEach((affectKey) => {
          if (!affectKey) return;
          nbRealiseesParMed[affectKey] = (nbRealiseesParMed[affectKey] || 0) + 1;
        });

        // Construction des rows
        const daysInMonth = new Date(YEAR, MONTH, 0).getDate();
        const newRows = [];
        for (let i = 1; i <= daysInMonth; i++) {
          const date = `${YEAR}-${String(MONTH).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
          const affectKey = liste[date] ?? null;
          const desidInfo = affectKey ? desidByKey[affectKey] : null;

          let medecinLabel = "Aucun médecin affecté";
          if (affectKey) {
            medecinLabel = mapKeyToLabel[affectKey] || affectKey;
          }

          const souhaitJour = desidInfo?.souhaits?.[date]?.label || desidInfo?.souhaits?.[date]?.code || "—";
          const nbGardesSouhaitees = desidInfo?.nbGardes > 0 ? desidInfo.nbGardes : "—";
          const nbGardesRealisees = affectKey ? nbRealiseesParMed[affectKey] || 0 : "—";

          newRows.push({
            date,
            notApplicable: isOrthoJunior && ![0, 6].includes(new Date(`${date}T12:00:00`).getDay()) && !isFrenchPublicHoliday(date),
            jour: jourSemaine(date),
            medecin: medecinLabel,
            affectKey,
            souhaitJour,
            nbGardesSouhaitees,
            nbGardesRealisees,
            hasMedecin: !!affectKey,
          });
        }

        setRows(newRows);

        // ✅ Comptage quadrimestre pour l'astreinte thoracique
        // Essaie d'abord la liste validée, puis fallback sur la brute
        if (isTho) {
          try {
            const quadriCounts = {};
            const quadriMois = ["2026-05", "2026-06", "2026-07", "2026-08"];
            for (const mc of quadriMois) {
              // Essayer la liste validée d'abord
              let res = await apiFetch(`/api/v2/listes-gardes-astreinte-thoracique/${mc}`);
              // Fallback sur la liste brute si pas de validée
              if (!res.ok) {
                res = await apiFetch(`/api/v2/listes-gardes-astreinte-thoracique/brute/${mc}`);
              }
              if (res.ok) {
                const data = await res.json();
                if (data.__meta) delete data.__meta;
                for (const [, medK] of Object.entries(data || {})) {
                  if (medK) quadriCounts[medK] = (quadriCounts[medK] || 0) + 1;
                }
              }
            }
            setNbQuadriParMed(quadriCounts);
          } catch (_e) {
            console.log("ℹ️ Impossible de charger les données quadrimestre");
          }
        }

        setLoading(false);
      } catch (e) {
        console.error("❌ Erreur chargement liste de garde :", e);
        setRows([]);
        setError(e.message || "Erreur inconnue");
        setLoading(false);
      }
    };

    load();
  }, [MOIS, YEAR, MONTH, typeGarde, juniorReadSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════
  // ANNULATION VALIDATION
  // ═══════════════════════════════════════════════════
  const annulerValidation = async () => {
    const msg = isAstreinte
      ? "Annuler la validation de la liste d'astreintes ?\n\nLes médecins ne verront plus la liste validée tant qu'elle n'aura pas été re-validée."
      : "Annuler la validation de la liste de garde ?\n\nLes médecins ne verront plus la liste validée tant qu'elle n'aura pas été re-validée.";
    const ok = (await confirmDialog(msg));
    if (!ok) return;

    try {
      setCanceling(true);
      setCancelMsg(null);

      const res = await apiFetch("/api/v2/liste_gardes/annuler_validation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mois: MOIS, type: typeGarde }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} annulation validation. ${txt}`);
      }

      setCancelMsg("✅ Validation annulée. Retour à la liste brute…");
      navigate(withCurrentBack(`/responsable/gardes-brute?mois=${encodeURIComponent(MOIS)}&type=${encodeURIComponent(typeGarde)}`));
    } catch (e) {
      console.error("❌ Erreur annulation validation :", e);
      notify(`Erreur lors de l'annulation de la validation : ${e.message || "Erreur inconnue"}`);
    } finally {
      setCanceling(false);
    }
  };

  // ═══════════════════════════════════════════════════
  // STYLES
  // ═══════════════════════════════════════════════════
  const containerStyle = useMemo(() => ({
    maxWidth: "1100px", margin: "40px auto", padding: "24px", fontFamily: "Inter, sans-serif",
  }), []);

  const btnTop = useMemo(() => ({
    marginBottom: 20, padding: "10px 18px", borderRadius: 10,
    border: "1px solid #111827", background: "#111827", color: "#ffffff",
    cursor: "pointer", fontWeight: 700,
  }), []);

  const thStyle = useMemo(() => ({
    padding: "8px", border: "1px solid #e5e7eb", background: "#f3f4f6",
    fontWeight: 700, fontSize: 14, textAlign: "left", whiteSpace: "nowrap",
  }), []);

  const tdStyle = useMemo(() => ({
    padding: "8px", border: "1px solid #f1f5f9", fontSize: 14,
  }), []);

  const btnBottom = useMemo(() => ({
    padding: "10px 18px", borderRadius: 10, cursor: "pointer", fontWeight: 700,
    border: "1px solid #111827", background: "#111827", color: "#ffffff",
  }), []);

  const btnBottomDanger = useMemo(() => ({
    padding: "10px 18px", borderRadius: 10,
    cursor: canceling ? "not-allowed" : "pointer", fontWeight: 800,
    border: "1px solid #991b1b", background: canceling ? "#9ca3af" : "#991b1b",
    color: "#ffffff", opacity: canceling ? 0.85 : 1,
  }), [canceling]);

  // ═══════════════════════════════════════════════════
  // MODALE "Liste non validée"
  // ═══════════════════════════════════════════════════
  const NotPublishedModal = () => {
    if (!showNotPublishedModal) return null;
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9999 }}
        onClick={() => setShowNotPublishedModal(false)}>
        <div style={{ width: "min(560px, 100%)", background: "#ffffff", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", padding: 18 }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
            {isAstreinte ? "Liste d'astreintes non validée" : "Liste de garde non validée"}
          </div>
          <div style={{ color: "#374151", lineHeight: 1.45 }}>
            {isAstreinte ? "La liste d'astreintes n'est pas encore validée par le responsable." : "La liste de garde n'est pas encore validée par le responsable."}
            <br />Elle apparaîtra dès qu'elle sera validée.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid #111827", background: "#111827", color: "#fff", fontWeight: 800, cursor: "pointer" }}
              onClick={() => setShowNotPublishedModal(false)}>OK</button>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  // RENDU — LOADING
  // ═══════════════════════════════════════════════════
  if (loading) {
    return (
      <div style={containerStyle}>
        <button style={btnTop} onClick={handleTopBack}>{backLabel}</button>
        <p>{loadingLabel}</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // RENDU — ERREUR
  // ═══════════════════════════════════════════════════
  if (error) {
    return (
      <div style={containerStyle}>
        <button style={btnTop} onClick={handleTopBack}>{topBackLabel}</button>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>{displayTitle}</h1>
        <p style={{ color: "#b91c1c", marginBottom: 12 }}>❌ Erreur lors du chargement de la liste : {error}</p>
        {error.includes("404") && (
          <p style={{ color: "#374151", marginTop: 8, padding: "12px", background: "#f3f4f6", borderRadius: 8 }}>
            ℹ️ La liste n'est pas encore validée pour ce mois ({monthLabel}).
            <br />Elle apparaîtra dès qu'elle sera validée par le responsable.
          </p>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // RENDU — NOT PUBLISHED (médecin)
  // ═══════════════════════════════════════════════════
  if (!isResponsable && notPublished) {
    return (
      <div style={containerStyle}>
        <NotPublishedModal />
        <button style={btnTop} onClick={handleTopBack}>{topBackLabel}</button>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>{displayTitle}</h1>
        <p style={{ color: "#374151", marginBottom: 16 }}>{displaySubtitle}</p>
        <div style={{ marginTop: 24 }}>
          <button style={btnBottom} onClick={() => navigate(typeGarde === "ASTREINTE_THORACIQUE" ? "/interface-astreinte-thoracique" : typeGarde === "PR1" ? "/interface-medecins-pr1" : "/interface-medecins")}>Retour interface Médecins</button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // HELPER : rendu d'une cellule médecin avec badge origine (responsable + tho)
  // ═══════════════════════════════════════════════════
  const renderMedecinCell = (r) => {
    if (!r.hasMedecin && r.notApplicable) {
      return <span style={{ color: "#6b7280" }}>Pas d’astreinte ce jour</span>;
    }
    if (!r.hasMedecin) {
      return <span style={{ color: "#b91c1c", fontWeight: 700 }}>Aucun médecin affecté</span>;
    }

    if (!isTho || !isResponsable) {
      return r.medecin;
    }

    // ✅ Affichage enrichi pour thoracique côté responsable
    const orig = r.affectKey ? mapKeyToOrigine[r.affectKey] : null;
    const isInt = orig === "interieur";
    const isExt = orig === "exterieur";
    const medBg = r.affectKey ? (isInt ? "#f0fdf4" : isExt ? "#eff6ff" : undefined) : undefined;
    const badgeColor = isInt ? "#166534" : isExt ? "#1e40af" : "#374151";
    const badgeBg = isInt ? "#dcfce7" : isExt ? "#dbeafe" : "#f3f4f6";
    const badgeLabel = isInt ? "Intérieur" : isExt ? "Extérieur" : "";

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: medBg, padding: "2px 6px", borderRadius: 6 }}>
        <span style={{ fontWeight: 600 }}>{r.medecin}</span>
        {badgeLabel && (
          <span style={{ fontSize: 11, fontWeight: 700, color: badgeColor, background: badgeBg, padding: "1px 7px", borderRadius: 6 }}>
            {badgeLabel}
          </span>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  // RENDU PRINCIPAL
  // ═══════════════════════════════════════════════════
  return (
    <div style={containerStyle}>
      <NotPublishedModal />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        {topBackPath && (
          <button style={btnTop} onClick={handleTopBack}>
            {backLabel || "← Retour"}
          </button>
        )}
        {isResponsable && <LogoutButton />}
      </div>

      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>{displayTitle}</h1>
      <p style={{ color: "#374151", marginBottom: 16 }}>{displaySubtitle}</p>

      {isResponsable && cancelMsg ? (
        <p style={{ margin: "0 0 14px", color: "#065f46", fontWeight: 800 }}>{cancelMsg}</p>
      ) : null}

      {/* Navigation quadrimestre supprimée — chaque mois est indépendant,
          la navigation se fait depuis la modale "Mois à venir" */}

      {/* ✅ Tableau */}
      {rows.length > 0 ? (
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e5e7eb", background: "#ffffff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
            <thead>
              <tr>
                {isResponsable ? (
                  <>
                    <th style={thStyle}>Jour</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>{isAstreinte ? "Médecin d'astreinte" : "Médecin de garde"}</th>
                    {!isAstreinte && <th style={thStyle}>Souhait initial sur ce jour</th>}
                    {!isAstreinte && <th style={thStyle}>Gardes souhaitées (total)</th>}
                    <th style={thStyle}>{isAstreinte ? "Astreintes (mois)" : "Gardes réalisées (total)"}</th>
                    {isTho && <th style={thStyle}>Astreintes (quadrimestre)</th>}
                  </>
                ) : (
                  <>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>{isAstreinte ? "Médecin d'astreinte" : "Médecin de garde"}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const blocJour = isJourBloc(r.date);
                const blocBg = blocJour ? "#fff7ed" : undefined;

                return (
                  <tr key={r.date + idx} style={blocJour ? { background: blocBg } : undefined}>
                    {isResponsable ? (
                      <>
                        <td style={{ ...tdStyle, ...(blocJour ? { fontWeight: 700, color: "#9a3412" } : {}) }}>{r.jour}</td>
                        <td style={{ ...tdStyle, ...(blocJour ? { fontWeight: 600, color: "#9a3412" } : {}) }}>
                          {new Date(r.date).toLocaleDateString("fr-FR")}
                        </td>
                        <td style={tdStyle}>{renderMedecinCell(r)}</td>
                        {!isAstreinte && <td style={tdStyle}>{r.souhaitJour}</td>}
                        {!isAstreinte && <td style={tdStyle}>{r.nbGardesSouhaitees}</td>}
                        <td style={tdStyle}>{r.nbGardesRealisees}</td>
                        {isTho && (
                          <td style={tdStyle}>
                            {r.affectKey ? (nbQuadriParMed[r.affectKey] || 0) : "—"}
                          </td>
                        )}
                      </>
                    ) : (
                      <>
                        <td style={tdStyle}>{new Date(r.date).toLocaleDateString("fr-FR")}</td>
                        <td style={tdStyle}>
                          {renderMedecinCell(r)}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: "24px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#f9fafb", textAlign: "center" }}>
          <p style={{ color: "#374151", fontSize: "16px", margin: 0 }}>
            ℹ️ Aucune liste validée disponible pour ce mois ({monthLabel}).
          </p>
        </div>
      )}

      {/* ✅ Bas de page : boutons d'actions */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
        {isResponsable ? (
          <>
            <button style={btnBottom}
              onClick={() => {
                const manualPath = manualJuniorListPath(user, typeGarde, MOIS);
                if (manualPath) window.location.assign(withResponsibleBridgeContext(manualPath, { backMode: 'current' }));
                else navigate(withCurrentBack(`/responsable/gardes-brute?mois=${encodeURIComponent(MOIS)}&type=${encodeURIComponent(typeGarde)}`));
              }}>
              Modifier la liste manuellement
            </button>

            <button style={btnBottom}
              onClick={() => window.open(`/api/v2/liste_gardes_valide/${MOIS}/pdf?type=${encodeURIComponent(typeGarde)}`, "_blank")}
              title="Télécharger le PDF de la liste validée">
              {isAstreinte ? "Télécharger PDF (astreintes validées)" : "Télécharger PDF (liste validée)"}
            </button>

            <button style={btnBottom}
              onClick={() => window.open(`/api/v2/liste_gardes_valide/${MOIS}/excel?type=${encodeURIComponent(typeGarde)}`, "_blank")}
              title="Télécharger le fichier Excel de la liste validée">
              {isAstreinte ? "Télécharger Excel (astreintes validées)" : "Télécharger Excel (liste validée)"}
            </button>

            {estValidee && canConstituteList ? (
              <button style={btnBottomDanger} onClick={annulerValidation} disabled={canceling}
                title="Annuler la validation (les médecins ne verront plus la liste validée)">
                {canceling ? "Annulation..." : "Annuler la validation"}
              </button>
            ) : null}

            <button style={btnBottom} onClick={() => navigate(withCurrentBack(`/responsable/gardes?type=${encodeURIComponent(typeGarde)}`))}>
              {isAstreinte ? "Retour à la gestion des astreintes" : "Retour à la gestion des gardes"}
            </button>
          </>
        ) : (
          <>
            <button style={btnBottom}
              onClick={() => window.open(`/api/v2/liste_gardes_valide/${MOIS}/pdf?type=${encodeURIComponent(typeGarde)}&view=simple`, "_blank")}
              title="Télécharger le PDF de la liste validée">
              Télécharger PDF
            </button>

            <button style={btnBottom}
              onClick={() => window.open(`/api/v2/liste_gardes_valide/${MOIS}/excel?type=${encodeURIComponent(typeGarde)}&view=simple`, "_blank")}
              title="Télécharger le fichier Excel de la liste validée">
              Télécharger Excel
            </button>

            <button style={btnBottom} onClick={() => navigate(isTho ? "/interface-astreinte-thoracique" : typeGarde === "PR1" ? "/interface-medecins-pr1" : "/interface-medecins")}>
              {isTho ? "Retour interface Astreintes" : "Retour interface Médecins"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
